import React, { useEffect } from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router';
import { Breadcrumb } from '../../hooks';
import {
  HStack,
  LinkText,
  ModalDesktop,
  Text,
} from '@adc/parallax-component-library';
import './BreadcrumbNav.scss';
import i18n from '../../utilities/i18n';
import { useNavigationPrompt } from '../../contexts/NavigationPromptContext';

/**
 * Displays the breadcrumb navigation as a horizontal list.
 * It shows the current path and allows users to navigate back to previous pages.
 * @returns {React.JSX.Element}
 */
export function BreadcrumbNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isFormDirty } = useNavigationPrompt();

  const {
    state: blockerState,
    reset,
    proceed,
  } = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isFormDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blockerState === 'blocked') {
      ModalDesktop.show({
        title: i18n.t('Common.unsavedChangesTitle'),
        details: i18n.t('Common.unsavedChangesMessage'),
        testID: 'unsaved-changes-modal',
        buttons: [
          {
            label: i18n.t('Common.stay'),
            variant: 'secondary',
            onPress: () => {
              ModalDesktop.hide();
              reset();
            },
          },
          {
            label: i18n.t('Common.leave'),
            variant: 'primary',
            onPress: () => {
              ModalDesktop.hide();
              proceed();
            },
          },
        ],
      });
    }
  }, [blockerState, proceed, reset]);

  const breadcrumbs: Breadcrumb[] = location.state?.breadcrumbs || [];

  const onBreadcrumbPress = (breadcrumbIndex: number) => {
    const numStepsBack = breadcrumbIndex - breadcrumbs.length + 1;

    navigate(numStepsBack);
  };

  return (
    <div className="breadcrumb-container">
      {!!breadcrumbs.length && (
        <nav>
          <HStack
            width="100%"
            padding="$4"
            paddingTop="$0"
            overflow="hidden"
            testID="breadcrumb-nav-container"
            backgroundColor="$neutral.10"
          >
            <HStack
              alignItems="center"
              gap="$2"
              width="100%"
              flexWrap="wrap"
              testID="breadcrumb-nav-items"
            >
              {breadcrumbs.map((breadcrumb: Breadcrumb, index: number) =>
                index === breadcrumbs.length - 1 ? (
                  <Text
                    key={index}
                    type="$bodySmall.default"
                    textWrap="nowrap"
                    testID="breadcrumb-nav-current"
                  >
                    {breadcrumb.label}
                  </Text>
                ) : (
                  <HStack key={index} alignItems="center" gap="$2">
                    <LinkText
                      type="$bodySmall.default"
                      onPress={() => onBreadcrumbPress(index)}
                      testID={`breadcrumb-nav-link-${index}`}
                    >
                      {breadcrumb.label}
                    </LinkText>
                    <Text
                      type="$bodyBase.default"
                      testID="breadcrumb-nav-separator"
                    >
                      {'/'}
                    </Text>
                  </HStack>
                ),
              )}
            </HStack>
          </HStack>
        </nav>
      )}
    </div>
  );
}
